import { createFileRoute } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ExternalLink, Phone, PhoneOff } from 'lucide-react';
import prettyMilliseconds from 'pretty-ms';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInterval } from 'usehooks-ts';

import { Transcript } from './-components/transcript';
import { useSimulatedCall } from './-components/use-simulated-call';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/call-simulation/')({
  component: CallSimulationRoute,
});

/** Nothing here dials: any number will do, and it is the booking's number. */
const DEFAULT_CALLER = '+48500100200';

function CallSimulationRoute() {
  return (
    <main className="mx-auto w-full max-w-5xl p-4 sm:p-6">
      <Suspense fallback={<Skeleton className="h-96 w-full" />}>
        <CallSimulation />
      </Suspense>
    </main>
  );
}

function CallSimulation() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const { data: status } = useSuspenseQuery(
    trpc.callSimulation.status.queryOptions(),
  );
  const call = useSimulatedCall();
  const [phone, setPhone] = useState(DEFAULT_CALLER);
  const [now, setNow] = useState(Date.now);

  useInterval(
    () => setNow(Date.now()),
    call.state === 'connected' ? 1_000 : null,
  );

  if (!status.enabled) {
    return (
      <p className="py-16 text-center text-sm text-muted-foreground">
        {t('Simulated calls are not set up for this clinic')}
      </p>
    );
  }

  const inCall = call.state !== 'idle' && call.state !== 'ended';
  const isValidPhone = /^\+\d{9,15}$/.test(phone);
  const stateLabel = {
    idle: t('Ready'),
    microphone: t('Allow the microphone in your browser'),
    connecting: t('Connecting…'),
    connected:
      call.mode === 'speaking'
        ? t('The assistant is speaking')
        : t('The assistant is listening'),
    ended: t('Call ended'),
  }[call.state];

  const callButton = inCall ? (
    <Button variant="destructive" onClick={call.hangUp}>
      <PhoneOff aria-hidden />
      {t('Hang up')}
    </Button>
  ) : (
    <Button disabled={!isValidPhone} onClick={() => void call.start(phone)}>
      <Phone aria-hidden />
      {t('Call')}
    </Button>
  );

  return (
    <div className="grid gap-4 md:grid-cols-[18rem_1fr]">
      <section className="flex h-fit flex-col gap-5 rounded-lg border border-border bg-background p-5">
        <div>
          <h1 className="text-lg font-semibold">{t('Call simulation')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'A call to the assistant through this browser’s microphone. Nothing is dialled.',
            )}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="caller">{t('Caller’s number')}</Label>
          <Input
            id="caller"
            inputMode="tel"
            value={phone}
            disabled={inCall}
            onChange={(event) => setPhone(event.target.value.trim())}
          />
        </div>

        {callButton}

        <div className="flex items-center justify-between gap-2 text-sm">
          <Badge variant={call.state === 'connected' ? 'default' : 'secondary'}>
            {stateLabel}
          </Badge>
          {call.state === 'connected' && call.startedAt !== null && (
            <span className="text-muted-foreground tabular-nums">
              {prettyMilliseconds(Math.max(now - call.startedAt, 0), {
                colonNotation: true,
                hideYearAndDays: true,
                secondsDecimalDigits: 0,
              })}
            </span>
          )}
        </div>

        {call.failed && (
          <p role="alert" className="text-sm text-destructive">
            {t(
              'The call failed. Check the microphone permission and try again.',
            )}
          </p>
        )}

        {status.calendarUrl !== null && (
          <a
            href={status.calendarUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {t('Open the calendar')}
            <ExternalLink className="size-3.5" aria-hidden />
          </a>
        )}
      </section>

      <section className="flex flex-col rounded-lg border border-border bg-background">
        <h2 className="border-b border-border px-4 py-3 text-sm font-medium">
          {t('Live conversation')}
        </h2>
        <Transcript lines={call.lines} drafts={call.drafts} />
      </section>
    </div>
  );
}
